import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useActiveBreakCoverRequests,
  useConfirmBreakCover,
  type BreakCoverRequest,
} from "@/hooks/useBreakCoverRequests";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Coffee, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** Mounted in Layout. Visible only to Admin / Owner. */
export function ManagerBreakCoverAlerts() {
  const { role } = useAuth();
  const isManager = role === "admin" || role === "super_admin";
  const { data: requests } = useActiveBreakCoverRequests();
  const confirm = useConfirmBreakCover();
  const [tick, setTick] = useState(0);

  // Re-render every 30s for the escalation timer.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Client-side escalation: if a request is still pending after 5 minutes and
  // hasn't been escalated yet, flip it to "escalated" and insert a follow-up.
  useEffect(() => {
    if (!isManager || !requests) return;
    const now = Date.now();
    requests.forEach(async (r) => {
      if (r.status !== "pending") return;
      if (r.escalated_at) return;
      const age = now - new Date(r.created_at).getTime();
      if (age < FIVE_MINUTES_MS) return;
      await supabase
        .from("break_cover_requests" as any)
        .update({ status: "escalated", escalated_at: new Date().toISOString() })
        .eq("id", r.id)
        .eq("status", "pending");
    });
  }, [requests, tick, isManager]);

  if (!isManager || !requests || requests.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[90] w-[360px] max-w-[calc(100vw-2rem)] space-y-2">
      {requests.map((r) => (
        <AlertCard key={r.id} req={r} onConfirm={() => confirm.mutate(r)} confirming={confirm.isPending} />
      ))}
    </div>
  );
}

function AlertCard({
  req,
  onConfirm,
  confirming,
}: {
  req: BreakCoverRequest;
  onConfirm: () => void;
  confirming: boolean;
}) {
  const isEscalated = req.status === "escalated";
  return (
    <div
      role="alert"
      className={`rounded-xl border-2 p-3 shadow-lg ${
        isEscalated
          ? "border-destructive bg-destructive/10 animate-pulse"
          : "border-[#B45309] bg-[#FEF3C7]"
      }`}
    >
      <div className="flex items-start gap-2">
        {isEscalated ? (
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        ) : (
          <Coffee className="h-5 w-5 text-[#92400E] shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${isEscalated ? "text-destructive" : "text-[#92400E]"}`}>
            {isEscalated ? "Break cover still unconfirmed" : "Break cover needed"}
          </p>
          <p className="text-xs text-[#5a3a0a] mt-1">
            <strong>{req.staff_name}</strong> in <strong>{req.zone_name}</strong> needs break cover.{" "}
            {req.zone_name} requires {req.required_adults} adult
            {req.required_adults === 1 ? "" : "s"} and would drop to {req.simulated_counted} without them
            {req.legal_ratio ? ` (${req.legal_ratio}).` : "."}
          </p>
          <p className="text-[10px] text-[#5a3a0a]/80 mt-1">
            Verbally arrange cover with another staff member, then confirm below.
          </p>
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              className="h-7 text-xs gap-1 bg-[#B45309] hover:bg-[#92400E]"
              disabled={confirming}
              onClick={onConfirm}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Cover arranged
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
